import { Card, CardContent } from '@/components/ui/Card.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import NoItems from '@/components/ui/NoItems.tsx';
import { BookingPublicSummaryDto } from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import { formatPriceList } from '@/utils/formatters.ts';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import BillLineRow from '@/features/public/bookingSummary/components/BillLineRow.tsx';
import ClientTile from '@/features/public/bookingSummary/components/ClientTile.tsx';
import ParticipantBillTile from '@/features/public/bookingSummary/components/ParticipantBillTile.tsx';

interface ParticipantsPriceCardProps {
  data: BookingPublicSummaryDto;
}

/**
 * Rachunek rezerwacji: kafel na uczestnika i pozycje całej rezerwacji pod nimi.
 *
 * Klient widział tu wcześniej jedną liczbę na osobę i musiał zadzwonić, żeby dowiedzieć się, z czego
 * ona jest. Kwoty biorą się z tej samej księgi, z której liczy je organizator — nie z drugiego
 * rachunku prowadzonego obok.
 */
const ParticipantsPriceCard = ({ data }: ParticipantsPriceCardProps) => (
  <div className="flex flex-col gap-2">
    <ClientTile client={data.client} />

    {data.participants.length === 0 ? (
      <Card>
        <CardContent className="py-4">
          <NoItems
            title="Brak uczestników"
            description="Rezerwacja nie ma już uczestników — zrezygnowali albo rezerwacja została anulowana."
          />
        </CardContent>
      </Card>
    ) : (
      data.participants.map((participant) => (
        <ParticipantBillTile key={participant.id} participant={participant} />
      ))
    )}

    {data.bookingAdjustments.length > 0 && (
      <Card>
        <CardContent className="pt-4">
          <div className={columnLabelClass}>Zniżki i korekty rezerwacji</div>
          <div className="mt-1">
            {data.bookingAdjustments.map((line, index) => (
              <BillLineRow key={`adjustment-${index}`} line={line} />
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <span className="font-semibold text-ink">Razem</span>
        <span className="font-semibold tabular-nums text-ink">
          {data.totalPrice.length > 0 ? (
            formatPriceList(data.totalPrice)
          ) : (
            <EmptyValue />
          )}
        </span>
      </CardContent>
    </Card>
  </div>
);

export default ParticipantsPriceCard;
